import { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Search, Award, ArrowLeft, Printer } from 'lucide-react';
import GlowingCard from '../components/ui/GlowingCard';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3062/api';

export default function Certificate({ onBack }) {
  const [tokenNumber, setTokenNumber] = useState('');
  const [certificateData, setCertificateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchCertificate = async (e) => {
    e.preventDefault();
    if (!tokenNumber) {
      setError('Please enter token number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/students/certificate/${tokenNumber}`);
      const data = await response.json();
      
      if (data.success) {
        setCertificateData(data);
      } else {
        setError(data.error || 'Token not found');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = () => {
    window.print();
  };

  if (certificateData) {
    const { student, rank, eventName, eventDate } = certificateData;
    
    return (
      <div className="min-h-screen bg-robo-dark text-white p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => {
              setCertificateData(null);
              setTokenNumber('');
              onBack?.();
            }}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>

          {/* Certificate */}
          {/* Certificate */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white text-black p-8 md:p-12 rounded-2xl shadow-2xl print:shadow-none print:p-0"
            id="certificate"
          >
            {/* School Logo */}
            <div className="flex justify-center mb-6">
              <img 
                src="/school-logo.png" 
                alt="Brightway International School"
                className="w-32 h-32 object-contain"
              />
            </div>

            {/* Certificate Header */}
            <div className="text-center mb-8 border-b-4 border-green-500 pb-6">
              <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-2">
                {eventName}
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-green-600">
                ROBO SOCCER - CHILDRENS DAY EVENT
              </h2>
              <p className="text-gray-600 mt-2">{eventDate}</p>
            </div>

            {/* Student Info */}
            <div className="text-center mb-8">
              <p className="text-gray-700 mb-2">This is to certify that</p>
              <h3 className="text-4xl md:text-5xl font-black text-green-600 mb-4 border-b-2 border-gray-300 inline-block pb-2">
                {student.name}
              </h3>
              <p className="text-xl text-gray-800">
                Class {student.className}
              </p>
              <p className="text-gray-700 mt-2">
                Token Number: <span className="font-bold">#{student.tokenNumber}</span>
              </p>
            </div>

            {/* Achievement */}
            <div className="text-center mb-8 p-6 bg-gray-50 rounded-xl">
              <p className="text-gray-700 mb-2">
                Successfully participated in the Robo Soccer Competition
              </p>
              {student.status === 'finished' && (
                <>
                  <p className="text-2xl font-bold text-gray-900 mb-2">
                    Score: {student.score} points
                  </p>
                  {rank && (
                    <p className="text-xl font-bold text-green-600">
                      Rank: {rank}{rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Footer with School Name */}
            <div className="text-center mt-12 pt-6 border-t-2 border-gray-300">
              <div className="flex justify-center mb-2">
                <Award className="w-12 h-12 text-green-500" />
              </div>
              <p className="text-lg font-bold text-gray-900">Brightway International School</p>
              <p className="text-sm text-gray-600">Robo Soccer Team</p>
            </div>

            {/* Certificate ID */}
            <div className="text-center mt-6 text-xs text-gray-500">
              <p>Certificate ID: ROBO-{student.tokenNumber}-{Date.now().toString().slice(-6)}</p>
              <p>Generated on: {new Date().toLocaleString()}</p>
            </div>
          </motion.div>
          {/* Download Button */}
          <div className="mt-6 flex gap-4 justify-center print:hidden">
            <button
              onClick={downloadCertificate}
              className="neon-button flex items-center gap-2"
            >
              <Download className="w-5 h-5" />
              Download / Print Certificate
            </button>
          </div>
        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            body * {
              visibility: hidden;
            }
            #certificate, #certificate * {
              visibility: visible;
            }
            #certificate {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 40px;
            }
            .print\\:hidden {
              display: none !important;
            }
          }
        `}</style>
      </div>
    );
  }

  // Search Form
  return (
    <div className="min-h-screen bg-robo-dark text-white p-4 md:p-8 flex items-center justify-center">
      <div className="max-w-md w-full">
        {/* Back Button */}
        <button
          onClick={() => onBack?.()}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        <GlowingCard className="p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-robo-green/20 flex items-center justify-center">
              <Award className="w-8 h-8 text-robo-green" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Download Certificate</h2>
            <p className="text-gray-400 text-sm">
              Enter your token number to get your certificate
            </p>
          </div>

          <form onSubmit={fetchCertificate} className="space-y-4">
            <div>
              <label className="block text-sm mb-2 text-gray-300">
                Token Number
              </label>
              <input
                type="number"
                className="token-input"
                placeholder="Enter your token number (e.g., 1001)"
                value={tokenNumber}
                onChange={(e) => setTokenNumber(e.target.value)}
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <motion.button
              type="submit"
              className="neon-button w-full flex items-center justify-center gap-2"
              disabled={loading}
              whileTap={{ scale: 0.98 }}
            >
              {loading ? (
                <span>Searching...</span>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  Get Certificate
                </>
              )}
            </motion.button>
          </form>

          <div className="mt-6 p-4 bg-white/5 rounded-lg">
            <p className="text-xs text-gray-400 text-center">
              💡 Tip: You can download your certificate anytime after completing the match
            </p>
          </div>
        </GlowingCard>
      </div>
    </div>
  );
}